/*
Copyright (c) 2012-2022 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useEffect, useRef, useCallback } from "react"
import _debounce from "lodash/debounce"
import _throttle from "lodash/throttle"

type ParamType = {
  startIdlingExecution: () => void
  backToActiveExecution: () => void
  waitTime: number // in milliseconds
  isDisabled?: boolean
}

const DEBOUNCE_WAIT_TIME = 2000 // 2S

const usePageIdleDetection = ({
  startIdlingExecution,
  backToActiveExecution,
  waitTime,
  isDisabled,
}: ParamType) => {
  const idleTimer = useRef<ReturnType<typeof setTimeout>>()
  const isIdling = useRef<boolean>(false)

  const startCountingIdleTime = useCallback(() => {
    if (idleTimer.current) {
      clearTimeout(idleTimer.current)

      if (isIdling.current) {
        backToActiveExecution()
        isIdling.current = false
      }
    }

    idleTimer.current = setTimeout(() => {
      if (!isIdling.current) {
        startIdlingExecution()
        isIdling.current = true
      }
    }, waitTime)
  }, [startIdlingExecution, backToActiveExecution, waitTime])

  const handleVisibilitychange = useCallback(() => {
    // in case using tablet, listen to mousemove only be triggered for tapping action, hence need to listen to browser tab change
    if (document.visibilityState === "visible" && isIdling.current) {
      backToActiveExecution()
      isIdling.current = false
    }
  }, [backToActiveExecution])

  useEffect(() => {
    // If disabled, clear any existing timer and reset idle state
    if (isDisabled) {
      if (idleTimer.current) {
        clearTimeout(idleTimer.current)
        idleTimer.current = undefined
      }
      if (isIdling.current) {
        isIdling.current = false
      }
      return () => {} // Return empty cleanup function
    }

    const startCountingIdleTimeDebounced = _debounce(
      startCountingIdleTime,
      DEBOUNCE_WAIT_TIME,
    )
    const startCountingIdleTimeThrottled = _throttle(
      startCountingIdleTime,
      DEBOUNCE_WAIT_TIME,
    )

    // if user move away from tab -> mousemove event does not trigger -> isIdling turns true after waiting time
    document.addEventListener("mousemove", startCountingIdleTimeDebounced)
    document.addEventListener("mousemove", startCountingIdleTimeThrottled) // throttle to reset debounce time if start moving the cursor after start counting idling time
    document.addEventListener("visibilitychange", handleVisibilitychange)

    return () => {
      startCountingIdleTimeDebounced.cancel()
      startCountingIdleTimeThrottled.cancel()
      document.removeEventListener("mousemove", startCountingIdleTimeDebounced)
      document.removeEventListener("mousemove", startCountingIdleTimeThrottled)
      document.removeEventListener("visibilitychange", handleVisibilitychange)
      if (idleTimer.current) clearTimeout(idleTimer.current)
    }
  }, [isDisabled, startCountingIdleTime, handleVisibilitychange])
}

export default usePageIdleDetection
